import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { COMPANY, tel, mail } from "@/data/company";
import { PRODUCTS } from "@/data/products";
import { SERVICES } from "@/data/services";

const Col = ({ title, children }) => (
  <div>
    <h3 className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-amber-brand mb-5">{title}</h3>
    <ul className="space-y-2.5">{children}</ul>
  </div>
);

const FLink = ({ to, children }) => (
  <li>
    <Link to={to} className="text-sm text-steel hover:text-steel-light">{children}</Link>
  </li>
);

export const Footer = () => (
  <footer className="bg-ink border-t border-line" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-10">
      <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr] mb-16">
        <div>
          <img src="/images/logo.png" alt="Vikas Rolls Private Limited logo" className="h-16 w-auto mb-6" />
          <p className="text-steel text-sm leading-relaxed max-w-sm mb-6">
            Vikas Rolls Private Limited manufactures and refurbishes industrial rolls for demanding
            production environments — controlling material selection, compounding, machining,
            covering and precision finishing under one roof since 1970.
          </p>
          <address className="not-italic space-y-3 text-sm text-steel">
            <p className="flex items-start gap-3">
              <MapPin size={16} className="text-amber-brand flex-none mt-0.5" aria-hidden="true" />
              <span>{COMPANY.address.lines.join(", ")}</span>
            </p>
            <p className="flex items-center gap-3">
              <Phone size={16} className="text-amber-brand flex-none" aria-hidden="true" />
              <a href={tel(COMPANY.phone.primary)} className="hover:text-steel-light" data-testid="footer-phone-link">{COMPANY.phone.primary}</a>
            </p>
            <p className="flex items-center gap-3">
              <MessageCircle size={16} className="text-amber-brand flex-none" aria-hidden="true" />
              <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" className="hover:text-steel-light" data-testid="footer-whatsapp-link">Chat on WhatsApp</a>
            </p>
            <p className="flex items-center gap-3">
              <Mail size={16} className="text-amber-brand flex-none" aria-hidden="true" />
              <a href={mail(COMPANY.emails.info)} className="hover:text-steel-light" data-testid="footer-email-link">{COMPANY.emails.info}</a>
            </p>
          </address>
        </div>

        <Col title="Products">
          {PRODUCTS.map((p) => <FLink key={p.slug} to={`/products/${p.slug}`}>{p.name}</FLink>)}
          <FLink to="/products">All Products</FLink>
        </Col>

        <Col title="Services">
          {SERVICES.slice(0, 6).map((s) => <FLink key={s.slug} to="/technical-services">{s.name}</FLink>)}
          <FLink to="/technical-services">All Services</FLink>
        </Col>

        <Col title="Company">
          <FLink to="/about">About Us</FLink>
          <FLink to="/industries">Industries</FLink>
          <FLink to="/elastomers">Elastomers</FLink>
          <FLink to="/quality">Quality</FLink>
          <FLink to="/contact">Contact</FLink>
          <FLink to="/request-a-quote">Request a Quote</FLink>
        </Col>
      </div>

      <div className="overflow-hidden select-none border-t border-line/60 pt-8" aria-hidden="true">
        <p className="font-display font-black tracking-tight text-[13vw] leading-[0.85] text-transparent whitespace-nowrap" style={{ WebkitTextStroke: "1px #26282b" }}>
          VIKAS ROLLS
        </p>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-t border-line/60 mt-8 pt-6">
        <p className="font-mono-tech text-[11px] text-steel/70 uppercase tracking-[0.15em]">
          © {new Date().getFullYear()} Vikas Rolls Private Limited. All rights reserved.
        </p>
        <p className="font-mono-tech text-[11px] text-steel/70 uppercase tracking-[0.15em]">
          Industrial roller expertise since 1970
        </p>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <Link
            to="/portal/login"
            data-testid="footer-staff-login-link"
            className="font-mono-tech text-[11px] text-steel/50 uppercase tracking-[0.15em] hover:text-amber-brand"
          >
            Staff Login
          </Link>
          <Link
            to="/roll-status"
            data-testid="footer-production-login-link"
            className="font-mono-tech text-[11px] text-steel/50 uppercase tracking-[0.15em] hover:text-amber-brand"
          >
            Production Login
          </Link>
        </div>
      </div>
    </div>
  </footer>
);
